// ─────────────────────────────────────────────────────────────────────────────
// Can I Trade? guide page
// ─────────────────────────────────────────────────────────────────────────────

import {
  GuideLayout, H2First, H2, P, UL, LI, UI,
  ScreenshotFrame, Callout,
} from "../GuideLayout";

export function GuideCanITrade() {
  return (
    <GuideLayout
      title="Can I Trade?"
      subtitle="A preflight checklist to run before placing an order. Five categories of yes/no questions, a weighted percentage score, a clear recommendation, and a calendar-aware view of today's clean trading windows."
      prev={{ slug: "trade-entry",  label: "Trade Entry" }}
      next={{ slug: "trade-review", label: "Trade Review" }}
    >
      <H2First>What it's for</H2First>
      <P>
        Most blown trades aren't analytical failures — they're state
        failures. You knew the setup. You knew the rules. But you were
        tired, distracted, or fired up from the last loss, and you
        took the trade anyway.
      </P>
      <P>
        Can I Trade? is a 60-second checklist designed to catch those
        moments. Five quick categories, weighted yes/no questions, a
        score, and a recommendation. Not analytics — just a pause
        point you can walk through before clicking buy or sell.
      </P>

      <H2>How to open it</H2>
      <P>
        From the Dashboard, click the green <UI>✅ Can I Trade?</UI>{" "}
        action card in the row alongside Log Trade and Review Last
        Trade.
      </P>

      <Callout kind="info" title="Subscription required">
        The checklist is behind the subscription gate. Non-subscribed
        users will see an upgrade card instead of the checklist when
        they click the action card. The checklist is fully accessible
        during your 30-day trial.
      </Callout>

      <H2>Account selector</H2>
      <P>
        At the top of the checklist page, below the title, is a{" "}
        <UI>Trading Account</UI> dropdown. Select the account you're
        about to trade before running through the questions.
      </P>
      <P>
        This matters for the behavioral patterns block that appears
        after you get your answer (see below) — patterns are scoped
        per account, so selecting the right one ensures you see the
        patterns relevant to the account you're actually trading.
        Changing it here doesn't affect any other part of the app.
      </P>

      <ScreenshotFrame
        src="/guide/can-i-trade.png"
        alt="Pre-Flight Check page with weighted questions across five categories"
        caption="Five categories of yes/no questions, each weighted, summed to a percentage score at the bottom."
      />

      <H2>The five categories</H2>
      <UL>
        <LI><UI>💪 Physical</UI> — sleep, hunger, basic body state.</LI>
        <LI><UI>🏠 Environmental</UI> — workspace clarity, freedom from interruptions.</LI>
        <LI><UI>🧠 Mental</UI> — settling-in time, warmup trades, mental carryover from yesterday's last trade.</LI>
        <LI><UI>❤️ Emotional</UI> — your relationship to losing this trade.</LI>
        <LI><UI>🛡️ Discipline</UI> — trading from a plan, willingness to walk away.</LI>
      </UL>

      <H2>How scoring works</H2>
      <P>
        Each question has a weight (shown as "Weight: 12" under the
        question text). Answer <UI>Yes</UI> and the weight counts
        toward your score. Answer <UI>No</UI> and it doesn't.
      </P>
      <P>
        Your running total displays as a <UI>Current Score: N%</UI>{" "}
        readout at the bottom of the page, updating as you click
        through. The <UI>Get My Answer →</UI> button stays disabled
        until every question has a Yes or No — there's no skipping.
      </P>

      <H2>Get My Answer</H2>
      <P>
        Click <UI>Get My Answer →</UI> and you get one of three reads
        based on your score:
      </P>
      <UL>
        <LI><strong>✅ TAKE THE TRADE</strong> — high score. Almost everything aligned. Take the trade with full conviction.</LI>
        <LI><strong>⚠️ PROCEED WITH CAUTION</strong> — middle score. The result names which questions you answered No to, with a suggestion to reduce size and be extra disciplined.</LI>
        <LI><strong>❌ DO NOT TRADE</strong> — low score. Too many problems. Lists your weak areas and suggests skipping this setup.</LI>
      </UL>

      <H2>Your Patterns to Watch</H2>
      <P>
        Below the verdict, the checklist surfaces your top behavioral
        patterns directly from your Analytics data — the same detector
        results that power the Analytics Dashboard. This block updates
        automatically every time your trade data changes.
      </P>
      <P>
        The top 3 patterns by estimated monthly cost are shown, highest
        cost first. Only patterns with <UI>moderate</UI> or{" "}
        <UI>strong</UI> confidence are included — patterns flagged as
        thin or insufficient are excluded so you only see findings
        your data actually supports. Each row shows:
      </P>
      <UL>
        <LI>The pattern name (e.g. <UI>Revenge Trading</UI>, <UI>Hour Cliff</UI>)</LI>
        <LI>The estimated monthly cost in red (e.g. <UI>Est. cost: −$420/mo</UI>)</LI>
        <LI>A reminder to stay aware of this pattern during the session</LI>
      </UL>

      <Callout kind="tip">
        This isn't another checklist — it's a mirror. You've already
        confirmed you're mentally and physically ready to trade. These
        are the specific habits your own trade history says to watch
        for today. Awareness at the right moment is the whole point.
      </Callout>

      <P>
        The footer note reads: <UI>Showing top 3 by estimated cost
        (moderate or strong confidence only). Thin/insufficient patterns
        excluded. See Analytics for the full picture.</UI> If no
        patterns meet the threshold, a green{" "}
        <UI>🎯 No active behavioral patterns detected. Clean slate.</UI>{" "}
        message shows instead.
      </P>

      <Callout kind="info" title="Numbers match Analytics">
        The estimated costs shown here are pulled from the same
        detector run as your Analytics Dashboard — not a separate
        calculation. If you see −$1,304/mo for a Risk pattern in
        Analytics, the same number appears here. The numbers will
        always match.
      </Callout>

      <H2>Today's Trading Windows</H2>
      <P>
        Below the behavioral patterns block, a <UI>🕐 Today's Trading
        Windows</UI> section shows the full trading day broken into
        six time blocks — from Pre-Open through Late Session — each
        color-coded based on today's high-impact economic calendar:
      </P>
      <UL>
        <LI><strong>✅ Clean</strong> — no high-impact events within 30 minutes of this window. Conditions are clear.</LI>
        <LI><strong>⚠️ Caution</strong> — a high-impact event falls within 30 minutes of the block boundary. Worth watching.</LI>
        <LI><strong>🚫 Avoid</strong> — a high-impact event falls inside the block or within 15 minutes of it. Stay out.</LI>
      </UL>
      <P>
        The block you're currently in is highlighted with a colored
        background and labeled <UI>You are in:</UI> at the top. If
        the current block is Caution or Avoid, a green hint line below
        it shows you the next Clean window and when it starts.
      </P>
      <P>
        Past blocks fade out automatically so your eye goes to what's
        ahead. If there are no high-impact events today, a green{" "}
        <UI>🎯 No high-impact events today — all windows are clean.</UI>{" "}
        banner replaces the block list.
      </P>

      <Callout kind="info" title="Same data as the Economic Calendar">
        The windows are driven by the same calendar feed powering the
        Economic Calendar widget on your Dashboard and Market page —
        no separate data source. The ±30 min buffer is fixed and
        matches the standard news-avoidance window most prop firms
        recommend. All times shown in ET.
      </Callout>

      <Callout kind="tip">
        Use this in combination with your verdict. A{" "}
        <UI>✅ TAKE THE TRADE</UI> result paired with a Clean current
        window is as clear a green light as you'll get. A{" "}
        <UI>⚠️ PROCEED WITH CAUTION</UI> result in an Avoid window is
        a strong signal to wait for the next clean block.
      </Callout>

      <H2>Edit Checklist</H2>
      <P>
        The <UI>✏️ Edit Checklist</UI> button opens the customization
        panel. From here you can edit question wording or weight, add
        new questions, remove questions, reorder with ▲ / ▼ arrows,
        and reset to the default 10 questions.
      </P>

      <H2>Positive vs negative questions</H2>
      <P>
        When adding a question, a <UI>Positive (+)</UI> /{" "}
        <UI>Negative (−)</UI> selector controls the scoring direction.
        Positive questions add to your score when you answer Yes.
        Negative questions subtract — useful for items like "Did I take
        more than 2 losses in a row today?" where a Yes is the risk signal.
      </P>

      <H2>When to use it</H2>
      <UL>
        <LI><strong>Before your first trade of the day</strong> — the highest-leverage moment.</LI>
        <LI><strong>After a loss</strong> — circuit-breaker against revenge trading.</LI>
        <LI><strong>Before a high-conviction trade</strong> — when you're tempted to size up, the checklist forces confirmation you're actually ready.</LI>
      </UL>

      <H2>What this is, and isn't</H2>
      <P>
        Can I Trade? is a process aid, not analytics. Your answers
        aren't saved or logged anywhere — each session is fresh. The
        checklist itself (items, weights, ordering) is saved per user,
        but individual run results are not. For the analytics half —
        patterns across your actual trades — head to <UI>Analytics</UI>.
      </P>
    </GuideLayout>
  );
}
